// src/components/MfaScreens.jsx
//
// Two screens that gate the main app for email/password users:
//   - MfaEnrollScreen: shown when user has no verified TOTP factor.
//     Generates a TOTP secret + QR code, asks the user to scan and
//     enter the first code to activate MFA.
//   - MfaChallengeScreen: shown when user has a verified factor but
//     the session is at aal1. Asks for the 6-digit code to upgrade
//     to aal2.
//
// Both screens are gating: the only way past them is verify-and-continue
// or sign out. SSO/OAuth users skip both since their IdP enforces MFA.

import React, { useEffect, useState, useCallback } from 'react';
import { ShieldCheck, KeyRound, AlertTriangle, Loader2, LogOut } from 'lucide-react';
import { supabase } from '../lib/supabase';

const PAGE_BG = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #0F172A 0%, #1A3665 100%)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
};
const CARD = {
  backgroundColor: 'white', borderRadius: '16px', maxWidth: '440px', width: '100%',
  padding: '2rem', boxShadow: '0 20px 50px rgba(15,23,42,0.3)',
};

function CodeInput({ value, onChange, onSubmit, disabled }) {
  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      value={value}
      onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      onKeyDown={e => { if (e.key === 'Enter' && value.length === 6) onSubmit(); }}
      disabled={disabled}
      placeholder="000000"
      className="w-full text-center text-2xl tabular-nums font-semibold tracking-[0.5em] py-3 rounded-lg focus:outline-none focus:ring-2"
      style={{ border: '1px solid #E5E9F0', color: '#0F172A', letterSpacing: '0.5em' }}
      autoFocus
    />
  );
}

export function MfaEnrollScreen({ user, onComplete, onSignOut }) {
  const [phase, setPhase] = useState('loading'); // loading|ready|verifying|done
  const [factor, setFactor] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);

  const runEnroll = useCallback((cancelledRef) => {
    (async () => {
      try {
        setPhase('loading');
        setError(null);
        // Clear any stale unverified factors first — just hygiene at this
        // point (see the unique friendlyName below for why enroll can't
        // actually collide with these anymore). Errors here are logged, not
        // thrown — a failed cleanup call used to silently leave an orphaned
        // factor around, whose blocked friendlyName was the only thing that
        // ever surfaced (as an opaque "already exists" error with no QR
        // code and no way to recover except signing out into the same
        // broken retry).
        const { data: existing } = await supabase.auth.mfa.listFactors();
        for (const f of (existing?.totp || [])) {
          if (f.status !== 'verified') {
            const { error: unenrollErr } = await supabase.auth.mfa.unenroll({ factorId: f.id });
            if (unenrollErr) console.warn('MFA cleanup: failed to unenroll stale factor', f.id, unenrollErr.message);
          }
        }
        // Unique per attempt so a leftover/orphaned factor (from a failed
        // cleanup above, or an interrupted prior attempt) can never block a
        // fresh enrollment with a "friendly name already exists" error.
        const { data, error } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
          friendlyName: `Authenticator-${Date.now().toString(36)}`,
        });
        if (cancelledRef.current) return;
        if (error) { setError(error.message); setPhase('ready'); return; }
        setFactor(data);
        setPhase('ready');
      } catch (e) {
        if (!cancelledRef.current) { setError(e.message); setPhase('ready'); }
      }
    })();
  }, []);

  useEffect(() => {
    const cancelledRef = { current: false };
    runEnroll(cancelledRef);
    return () => { cancelledRef.current = true; };
  }, [runEnroll]);

  const verify = async () => {
    if (!factor || code.length !== 6) return;
    setPhase('verifying');
    setError(null);
    try {
      const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId: factor.id });
      if (chErr) throw chErr;
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId: factor.id, challengeId: ch.id, code,
      });
      if (vErr) throw vErr;
      setPhase('done');
      // Small delay so the user sees the success state before being routed away
      setTimeout(() => onComplete(), 600);
    } catch (e) {
      setError(e.message || 'Invalid code — try again');
      setPhase('ready');
      setCode('');
    }
  };

  return (
    <div style={PAGE_BG}>
      <div style={CARD}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
               style={{ background: 'linear-gradient(135deg, #1A3665, #2E4B7C)' }}>
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight" style={{ color: '#0F172A' }}>
              Set up two-factor authentication
            </h1>
            <p className="text-xs" style={{ color: '#64748B' }}>
              Required for password-based accounts · {user?.email}
            </p>
          </div>
        </div>

        <div className="rounded-lg p-3 mb-4 text-[12px]"
             style={{ backgroundColor: '#EFF4FB', border: '1px solid #DBE6F5', color: '#1A3665' }}>
          Scan the QR code with an authenticator app like <strong>Google Authenticator</strong>,
          <strong> 1Password</strong>, <strong>Authy</strong>, or <strong>Microsoft Authenticator</strong>.
          Then enter the 6-digit code it shows.
        </div>

        {phase === 'loading' && (
          <div className="py-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#1A3665' }} />
          </div>
        )}

        {phase !== 'loading' && factor && (
          <>
            <div className="flex justify-center mb-3 p-3 rounded-lg" style={{ backgroundColor: '#FAFBFD', border: '1px solid #F0F2F6' }}>
              <div dangerouslySetInnerHTML={{ __html: factor.totp.qr_code }} style={{ width: 200, height: 200 }} />
            </div>
            <details className="mb-4">
              <summary className="text-[12px] cursor-pointer" style={{ color: '#64748B' }}>
                Can't scan? Enter the secret manually
              </summary>
              <div className="mt-2 p-2 rounded text-[11px] font-mono break-all"
                   style={{ backgroundColor: '#F1F5F9', color: '#475569' }}>
                {factor.totp.secret}
              </div>
            </details>
            <label className="text-[11px] font-semibold uppercase tracking-wider block mb-1.5"
                   style={{ color: '#64748B' }}>
              Enter the 6-digit code
            </label>
            <CodeInput value={code} onChange={setCode} onSubmit={verify} disabled={phase === 'verifying' || phase === 'done'} />
          </>
        )}

        {error && (
          <div className="mt-3 rounded-lg px-3 py-2 text-[12px] flex items-start gap-2"
               style={{ backgroundColor: '#FEE2E2', border: '1px solid #FECACA', color: '#B91C1C' }}>
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {error && !factor && (
          <button
            type="button"
            onClick={() => runEnroll({ current: false })}
            className="w-full mt-3 h-10 rounded-lg text-[13px] font-medium border"
            style={{ color: '#1A3665', borderColor: '#DBE6F5' }}
          >
            Try again
          </button>
        )}

        <button
          type="button"
          onClick={verify}
          disabled={!factor || code.length !== 6 || phase === 'verifying' || phase === 'done'}
          className="w-full mt-4 h-11 rounded-lg text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          style={{ background: 'linear-gradient(135deg, #1A3665, #2E4B7C)' }}
        >
          {phase === 'verifying' && <Loader2 className="w-4 h-4 animate-spin" />}
          {phase === 'done' && <ShieldCheck className="w-4 h-4" />}
          {phase === 'done' ? 'Activated' : phase === 'verifying' ? 'Verifying…' : 'Verify and continue'}
        </button>

        <button
          type="button"
          onClick={onSignOut}
          className="w-full mt-2 h-10 rounded-lg text-[13px] font-medium inline-flex items-center justify-center gap-1.5"
          style={{ color: '#64748B' }}
        >
          <LogOut className="w-3.5 h-3.5" /> Sign out instead
        </button>
      </div>
    </div>
  );
}

export function MfaChallengeScreen({ user, factorId, onComplete, onSignOut }) {
  const [code, setCode] = useState('');
  const [phase, setPhase] = useState('idle'); // idle|verifying|done
  const [error, setError] = useState(null);

  const verify = async () => {
    if (!factorId || code.length !== 6) return;
    setPhase('verifying');
    setError(null);
    try {
      const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
      if (error) throw error;
      setPhase('done');
      setTimeout(() => onComplete(), 400);
    } catch (e) {
      setError(e.message || 'Invalid code — try again');
      setPhase('idle');
      setCode('');
    }
  };

  return (
    <div style={PAGE_BG}>
      <div style={CARD}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
               style={{ background: 'linear-gradient(135deg, #1A3665, #2E4B7C)' }}>
            <KeyRound className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight" style={{ color: '#0F172A' }}>
              Two-factor verification
            </h1>
            <p className="text-xs" style={{ color: '#64748B' }}>{user?.email}</p>
          </div>
        </div>

        <p className="text-[13px] mb-4" style={{ color: '#475569' }}>
          Enter the 6-digit code from your authenticator app.
        </p>

        <CodeInput value={code} onChange={setCode} onSubmit={verify} disabled={phase !== 'idle'} />

        {error && (
          <div className="mt-3 rounded-lg px-3 py-2 text-[12px] flex items-start gap-2"
               style={{ backgroundColor: '#FEE2E2', border: '1px solid #FECACA', color: '#B91C1C' }}>
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="button"
          onClick={verify}
          disabled={code.length !== 6 || phase !== 'idle'}
          className="w-full mt-4 h-11 rounded-lg text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(135deg, #1A3665, #2E4B7C)' }}
        >
          {phase === 'verifying' && <Loader2 className="w-4 h-4 animate-spin" />}
          {phase === 'done' && <ShieldCheck className="w-4 h-4" />}
          {phase === 'done' ? 'Verified' : phase === 'verifying' ? 'Verifying…' : 'Verify'}
        </button>

        <button
          type="button"
          onClick={onSignOut}
          className="w-full mt-2 h-10 rounded-lg text-[13px] font-medium inline-flex items-center justify-center gap-1.5"
          style={{ color: '#64748B' }}
        >
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>

        <p className="text-[11px] mt-4 text-center" style={{ color: '#94A3B8' }}>
          Lost access to your authenticator? Contact an admin to reset it.
        </p>
      </div>
    </div>
  );
}
